import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Share as ShareIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../../hooks/useTranslation';
import postsTranslations from '../../posts/translate/fr.json';

const Posts = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t } = useTranslation();
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Get posts data from translations
  const postsData = postsTranslations.posts;
  const articlesData = postsTranslations.articles;

  useEffect(() => {
    if (slug && articlesData[slug]) {
      setSelectedArticle(articlesData[slug]);
    }
  }, [slug, articlesData]);

  // Function to get the appropriate image for each article
  const getArticleImage = (articleSlug) => {
    const imageMap = {
      'eyelash-extensions-seo': '/images/blog/cils.png',
      'pose-ongles-gel-seo': '/images/blog/pose-ongles-gel.png',
      'epilation-laser-seo': '/images/blog/epilation-laser.png',
      'soins-visage-seo': '/images/blog/soins-visage.png',
      'microblading-sourcils-seo': '/images/blog/microblading-sourcils.png',
      'massage-relaxant-seo': '/images/blog/massage-relaxant.png'
    };
    return imageMap[articleSlug] || '/images/blog/cils.png'; // fallback to default image
  };

  const handleShareArticle = (platform, article) => {
    const url = `${window.location.origin}/posts/${slug || 'eyelash-extensions-seo'}`;
    const title = article.title;
    
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to render text with internal links
  const renderTextWithLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the link
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        <Link
          key={match.index}
          component="button"
          onClick={() => navigate(linkUrl)}
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark'
            }
          }}
        >
          {linkText}
        </Link>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 1 ? parts : text;
  };

  const renderArticleContent = (content) => {
    return Object.entries(content).map(([key, section]) => {
      if (key === 'intro') {
        return (
          <Box key={key} sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              {section.split('\n').map((paragraph, index) => (
                <React.Fragment key={index}>
                  {paragraph}
                  {index < section.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
          </Box>
        );
      }

      if (typeof section === 'object' && section.title && section.content) {
        return (
          <Box key={key} sx={{ mb: 4 }}>
            <Typography variant="h2" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
              {section.title}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              {section.content.split('\n').map((paragraph, index) => {
                if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                  return (
                    <Typography key={index} variant="h3" component="h3" sx={{ mt: 3, mb: 2, fontWeight: 'bold', fontSize: '1.4rem' }}>
                      {paragraph.replace(/\*\*/g, '')}
                    </Typography>
                  );
                }
                if (paragraph.startsWith('•')) {
                  return (
                    <Typography key={index} component="li" sx={{ ml: 2, mb: 1 }}>
                      {renderTextWithLinks(paragraph.substring(2))}
                    </Typography>
                  );
                }
                if (paragraph.startsWith('✅')) {
                  return (
                    <Typography key={index} component="div" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: 8 }}>✅</span>
                      {renderTextWithLinks(paragraph.substring(2))}
                    </Typography>
                  );
                }
                return paragraph ? (
                  <React.Fragment key={index}>
                    {renderTextWithLinks(paragraph)}
                    <br />
                  </React.Fragment>
                ) : (
                  <br key={index} />
                );
              })}
            </Typography>
          </Box>
        );
      }

      return null;
    });
  };

  // Generate structured data for SEO
  const generateStructuredData = (article) => {
    return {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.metaDescription,
      "image": `${window.location.origin}/images/blog/extension-de-cils-salon.jpg`,
      "author": {
        "@type": "Organization",
        "name": article.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "BookingBarber",
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/Logos/logoFocus.png`
        }
      },
      "datePublished": article.publishDate,
      "dateModified": article.publishDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${window.location.origin}/posts/${slug}`
      },
      "keywords": article.tags.join(", "),
      "articleSection": postsData.categories[article.category],
      "wordCount": article.content ? Object.values(article.content).join(' ').split(' ').length : 0,
      "timeRequired": `PT${article.readingTime}M`
    };
  };

  // If viewing a specific article
  if (slug && selectedArticle) {
    const structuredData = generateStructuredData(selectedArticle);
    const currentUrl = `${window.location.origin}/posts/${slug}`;

    return (
      <>
        <Helmet>
          <title>{selectedArticle.title} | BookingBarber Blog</title>
          <meta name="description" content={selectedArticle.metaDescription} />
          <meta name="keywords" content={selectedArticle.tags.join(', ')} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="article" />
          <meta property="og:url" content={currentUrl} />
          <meta property="og:title" content={selectedArticle.title} />
          <meta property="og:description" content={selectedArticle.metaDescription} />
          <meta property="og:image" content={`${window.location.origin}/images/blog/extension-de-cils-salon.jpg`} />
          <meta property="og:site_name" content="BookingBarber" />
          <meta property="article:published_time" content={selectedArticle.publishDate} />
          <meta property="article:author" content={selectedArticle.author} />
          <meta property="article:section" content={postsData.categories[selectedArticle.category]} />
          {selectedArticle.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:url" content={currentUrl} />
          <meta name="twitter:title" content={selectedArticle.title} />
          <meta name="twitter:description" content={selectedArticle.metaDescription} />
          <meta name="twitter:image" content={`${window.location.origin}/images/blog/extension-de-cils-salon.jpg`} />

          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content={selectedArticle.author} />
          <link rel="canonical" href={currentUrl} />
          
          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>

        <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
          {/* Header */}
          <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', py: 2 }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/posts')}
                  sx={{ color: 'text.secondary' }}
                >
                  {postsData.backToPosts}
                </Button>
                <Breadcrumbs>
                  <Link color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                    Accueil
                  </Link>
                  <Link color="inherit" onClick={() => navigate('/posts')} sx={{ cursor: 'pointer' }}>
                    {postsData.title}
                  </Link>
                  <Typography color="text.primary">Extension de cils</Typography>
                </Breadcrumbs>
              </Box>
            </Container>
          </Box>

          <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Article Header */}
            <Paper sx={{ p: 4, mb: 4 }}>
              <Typography variant="h1" component="h1" sx={{ mb: 3, fontWeight: 'bold', lineHeight: 1.2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                {selectedArticle.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedArticle.author}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(selectedArticle.publishDate)} • {selectedArticle.readingTime} {postsData.readingTime}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {selectedArticle.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  {postsData.shareArticle}:
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleShareArticle('facebook', selectedArticle)}
                  sx={{ color: '#1877f2' }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleShareArticle('twitter', selectedArticle)}
                  sx={{ color: '#1da1f2' }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleShareArticle('linkedin', selectedArticle)}
                  sx={{ color: '#0077b5' }}
                >
                  <LinkedInIcon />
                </IconButton>
              </Box>
            </Paper>

            {/* Featured Image */}
            <Paper sx={{ mb: 4, overflow: 'hidden' }}>
              <img
                src={getArticleImage(slug)}
                alt={selectedArticle.title}
                style={{
                  width: '100%',
                  height: '300px',
                  objectFit: 'cover',
                  objectPosition: 'center top'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Paper>

            {/* Article Content */}
            <Paper sx={{ p: 4 }}>
              <article>
                {renderArticleContent(selectedArticle.content)}
              </article>

              <Divider sx={{ my: 4 }} />

              {/* Call to Action */}
              <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Prête à passer à l'étape supérieure ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Découvrez comment BookingBarber peut transformer votre activité d'extension de cils
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/auth?mode=signup')}
                  sx={{
                    backgroundColor: '#f5f5dc',
                    color: 'black',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#e8e8c8'
                    }
                  }}
                >
                  Tester gratuitement pendant 15 jours
                </Button>
              </Box>
            </Paper>
          </Container>
        </Box>
      </>
    );
  }

  // Blog listing page
  const blogListStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "BookingBarber Blog",
    "description": "Conseils et astuces pour les barbiers et coiffeurs : SEO, marketing digital, gestion de salon",
    "url": `${window.location.origin}/posts`,
    "publisher": {
      "@type": "Organization",
      "name": "BookingBarber",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/Logos/logoFocus.png`
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Blog BookingBarber - Conseils pour barbiers et coiffeurs</title>
        <meta name="description" content="Découvrez nos conseils SEO, marketing digital et gestion pour développer votre salon de barbier. Articles spécialisés pour barbiers et coiffeurs hommes." />
        <meta name="keywords" content="blog barbier, conseils salon, SEO barbier, marketing barbier, gestion salon, corte cheveux, shave" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/posts`} />
        <meta property="og:title" content="Blog BookingBarber - Conseils pour barbiers et coiffeurs" />
        <meta property="og:description" content="Découvrez nos conseils SEO, marketing digital et gestion pour développer votre salon de barbier." />
        <meta property="og:image" content={`${window.location.origin}/Logos/logoFocus.png`} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Blog BookingBarber - Conseils pour professionnels de la beauté" />
        <meta name="twitter:description" content="Découvrez nos conseils SEO, marketing digital et gestion pour développer votre salon de beauté." />

        <link rel="canonical" href={`${window.location.origin}/posts`} />
        
        <script type="application/ld+json">
          {JSON.stringify(blogListStructuredData)}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        {/* Header */}
        <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', py: 4 }}>
          <Container maxWidth="lg">
            <Typography variant="h1" component="h1" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '2rem', md: '3rem' } }}>
              {postsData.title}
            </Typography>
            <Typography variant="h2" component="p" sx={{ color: 'text.secondary', fontSize: '1.2rem', fontWeight: 'normal' }}>
              Conseils et astuces pour développer votre activité de beauté
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            {Object.entries(articlesData).map(([articleSlug, article]) => (
              <Grid item xs={12} md={6} lg={4} key={articleSlug}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate(`/posts/${articleSlug}`)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={getArticleImage(articleSlug)}
                    alt={article.title}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={postsData.categories[article.category]} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold', lineHeight: 1.3 }}>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {article.excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(article.publishDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.readingTime} min
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Posts;